import { AdmissionOfferStatus } from 'AdmissionOffer';
import { AoiRecordType } from 'ProgramAoi';
import { isPendingProgramApplication } from 'ProgramApplication';
import { ProgramInclusionStatus } from 'ProgramInclusion';
import {
    type CurrentUserIguanaObject,
    getAoiRecord,
    getIsDeclinedAdmissionOffer,
    getIsRejectedProgramApplication,
} from 'Users';
import { UnlockedTutorBotChatState } from 'TutorBotChat';
import { type BotPageState, LockedBotPageState, InvalidBotPageState } from './TutorBotAiAdvisorChat.types';

// This function only gets called if we've already determined that there is not a global
// reason for TutorBot to be locked, as defined in determineTutorBotLockedState. See how this is
// used in useBotPageStatusConfig.
// eslint-disable-next-line max-lines-per-function
export function determineBotPageState(
    currentUser: CurrentUserIguanaObject | null,
    botPageAccessForProspectiveStudents: boolean,
): BotPageState {
    if (!currentUser?.hasBotPageAccess) return InvalidBotPageState.NoAccess;

    const programApplication = getAoiRecord(currentUser, AoiRecordType.ProgramApplication);
    const admissionOffer = getAoiRecord(currentUser, AoiRecordType.AdmissionOffer);
    const programInclusion = getAoiRecord(currentUser, AoiRecordType.ProgramInclusion);

    const pendingApplication = !!programApplication && isPendingProgramApplication(programApplication);
    const isRejectedProgramApplication = getIsRejectedProgramApplication(currentUser);
    const isDeclinedAdmissionOffer = getIsDeclinedAdmissionOffer(currentUser);

    if (currentUser.canUseBotPageWithoutProgramInclusion) return UnlockedTutorBotChatState.IncludedOrGraduated;

    if (programInclusion?.status === ProgramInclusionStatus.Included) {
        if (botPageAccessForProspectiveStudents) {
            if (programInclusion.current) {
                return UnlockedTutorBotChatState.IncludedOrGraduated;
            }

            // before cohort content unlocked
            return UnlockedTutorBotChatState.RegisteredNotCurrent;
        }

        // note that this is a slightly different condition from above -- if THIS inclusion's
        // content is locked (say, for a billing issue), then we show the LockedBotPageState,
        // even if the cohort's content is unlocked.
        if (programInclusion.contentUnlocked) {
            return UnlockedTutorBotChatState.IncludedOrGraduated;
        }

        return LockedBotPageState.RegisteredNotCurrent;
    }

    if (programInclusion?.status === ProgramInclusionStatus.Graduated) {
        return UnlockedTutorBotChatState.IncludedOrGraduated;
    }

    // From here down, everyone is unlocked if botPageAccessForProspectiveStudents is true, and locked otherwise
    const baseChatState = botPageAccessForProspectiveStudents ? UnlockedTutorBotChatState : LockedBotPageState;

    if (programInclusion?.status === ProgramInclusionStatus.WillNotGraduate) {
        return baseChatState.ExpelledOrWithdrawn;
    }

    if (admissionOffer?.status === AdmissionOfferStatus.OfferedAdmission) {
        return baseChatState.OfferedAdmission;
    }

    if (isRejectedProgramApplication) {
        return botPageAccessForProspectiveStudents
            ? UnlockedTutorBotChatState.NotOfferedAdmission
            : LockedBotPageState.NotJoiningProgram;
    }

    if (isDeclinedAdmissionOffer) {
        return botPageAccessForProspectiveStudents
            ? UnlockedTutorBotChatState.OfferExpired
            : LockedBotPageState.NotJoiningProgram;
    }

    if (pendingApplication && programApplication.invitedToInterview && !programApplication.interviewScheduled) {
        return baseChatState.AppliedInterviewPending;
    }

    if (pendingApplication) {
        return baseChatState.Applied;
    }

    return baseChatState.Signup;
}
